import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatGraph, GraphNode } from '@aegislab/shared';
import { contextNodes } from '@aegislab/shared';

/**
 * 도깨비 그래프 store (강등) — 선형 채팅(assistantStore)과 별개의 자족 surface.
 *
 * 노드는 *실 대화*(messagesToGraph(useAssistantMessages(...)))에서 파생한다.
 * 이 store 는 클라 뷰상태만 보유한다:
 *   - selectedId        : 현재 선택 노드(전송 컨텍스트의 selectedId)
 *   - pinnedIds         : 위치 무관 상시 맥락 핀(클라 소유, 전송마다 DTO 로 전달)
 *   - collapsedIds      : 접힌 노드(자식 서브트리 숨김)
 *
 * GraphWorkspace 가 setGraph 로 실 그래프를 주입하면, 뷰상태(pin/collapse)를 파생 노드에
 * 오버레이한다(messagesToGraph 는 항상 pinned:false/collapsed:false 로 만든다).
 *
 * 이전 mock send(addExchange / branch / mockAnswer / seedDemo / lastSentPayload)는 제거됨.
 * 실 전송은 GraphComposer → useSendAssistantMessage(fromMessageId=selected, pinnedMessageIds=pinned).
 * 영속 대상 = 뷰상태(selectedId / pinnedIds / collapsedIds)만 — 실 노드는 매 진입 시 재파생.
 */

// v2: 대화별 뷰상태(byConversation). 이전 'dokkaebi-graph-proto'(전역 flat)는 형상이 달라
// 키를 바꿔 자연 폐기(전역 뷰상태는 버그였으므로 마이그레이션 불필요).
const STORAGE_KEY = 'dokkaebi-graph-view';

const EMPTY_GRAPH: ChatGraph = {
  id: 'dokkaebi-graph-proto',
  title: '도깨비 그래프',
  nodes: [],
  lastActiveId: null,
};

/** 화면상 노드 색 의미. 'pinned'·'sent' 둘 다 맥락(전송됨), 'excluded' 만 제외. */
export type NodeContextColor = 'pinned' | 'sent' | 'excluded';

/**
 * 척추 함수(시각면) — 노드 하나의 색.
 *
 * 전송 payload 와 *같은* contextNodes(graph, selectedId) 만 소비한다:
 *  - 맥락에 없으면        → 'excluded'(회색).
 *  - 맥락에 있고 pinned   → 'pinned'(별색, 위치 무관 상시 맥락).
 *  - 맥락에 있고 비핀     → 'sent'(파랑, 이번 전송 경로).
 *
 * 불변식: contextColor(...) !== 'excluded' 인 노드 집합
 *        === shared serializeContext 가 직렬화하는 노드 집합 (둘 다 contextNodes 를 소비).
 */
export function contextColor(
  graph: ChatGraph,
  selectedId: string | null,
  node: GraphNode
): NodeContextColor {
  const inContext = contextNodes(graph, selectedId).some((n) => n.id === node.id);
  if (!inContext) return 'excluded';
  return node.pinned ? 'pinned' : 'sent';
}

/** 한 대화의 그래프 뷰상태. */
interface ConvView {
  selectedId: string | null;
  pinnedIds: string[];
  collapsedIds: string[];
}

const EMPTY_VIEW: ConvView = { selectedId: null, pinnedIds: [], collapsedIds: [] };

interface GraphState {
  /**
   * 현재 파생 그래프(뷰상태 오버레이 적용 후). GraphWorkspace 가 setGraph 로 주입.
   * 영속 대상 아님 — 매 진입 시 messagesToGraph 로 재파생.
   */
  graph: ChatGraph;
  /** 현재 활성 대화 id(= graph.id). 뷰상태 스코프 기준. */
  activeConversationId: string | null;
  /** 대화별 뷰상태 — 대화 전환·재진입 시 선택/핀/접기를 기억(영속 대상). */
  byConversation: Record<string, ConvView>;
  // ── 활성 대화 뷰의 flat 미러(소비처 GraphMap/GraphComposer/NodeDetail 이 읽음) ──
  selectedId: string | null;
  /** 핀 노드 id 집합(클라 소유, 전송마다 pinnedMessageIds 로 전달). */
  pinnedIds: string[];
  /** 접힌 노드 id 집합. */
  collapsedIds: string[];
  /** 실 대화 그래프 주입 — graph.id 로 대화 식별, 그 대화의 저장 뷰를 복원/정리해 오버레이. */
  setGraph: (graph: ChatGraph) => void;
  /** selectedId 설정 + graph.lastActiveId 갱신(활성 대화 뷰에 기록). */
  select: (id: string | null) => void;
  /** 핀 토글 — 즉시 오버레이 + 활성 대화 뷰에 기록. */
  togglePin: (id: string) => void;
  /** 접힘 토글 — 즉시 오버레이 + 활성 대화 뷰에 기록. */
  toggleCollapse: (id: string) => void;
  /** 현재 대화 뷰 초기화(다른 대화 저장 뷰는 보존). */
  reset: () => void;
  /**
   * 계정 전환/로그아웃 시 전체 초기화 — reset()과 달리 byConversation(대화별 저장 뷰)까지
   * 비운다. persist된 이전 계정 대화 ID가 남아 새 계정이 그 대화를 열려다 실패하는 누수 차단.
   */
  resetForAccountSwitch: () => void;
}

/** 파생 그래프에 뷰상태(pinnedIds/collapsedIds)를 오버레이한다. */
function overlayViewState(
  graph: ChatGraph,
  pinnedIds: string[],
  collapsedIds: string[]
): ChatGraph {
  const pinned = new Set(pinnedIds);
  const collapsed = new Set(collapsedIds);
  return {
    ...graph,
    nodes: graph.nodes.map((n) => ({
      ...n,
      pinned: pinned.has(n.id),
      collapsed: collapsed.has(n.id),
    })),
  };
}

function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

/** 저장 뷰를 현재 그래프 노드에 맞게 정리 — 더 이상 없는 노드 id 는 제거(stale 방지). */
function pruneView(view: ConvView, graph: ChatGraph): ConvView {
  const ids = new Set(graph.nodes.map((n) => n.id));
  return {
    selectedId: view.selectedId && ids.has(view.selectedId) ? view.selectedId : null,
    pinnedIds: view.pinnedIds.filter((id) => ids.has(id)),
    collapsedIds: view.collapsedIds.filter((id) => ids.has(id)),
  };
}

/** 활성 대화 뷰를 byConversation 에 기록(convId 없으면 그대로). */
function writeView(
  byConversation: Record<string, ConvView>,
  convId: string | null,
  view: ConvView
): Record<string, ConvView> {
  if (!convId) return byConversation;
  return { ...byConversation, [convId]: view };
}

export const useGraphStore = create<GraphState>()(
  persist(
    (set) => ({
      graph: EMPTY_GRAPH,
      activeConversationId: null,
      byConversation: {},
      selectedId: null,
      pinnedIds: [],
      collapsedIds: [],
      setGraph: (graph) =>
        set((s) => {
          const convId = graph.id || null;
          let byConversation = s.byConversation;
          // 대화 전환: 떠나는 대화의 현재 뷰를 먼저 저장.
          if (s.activeConversationId && s.activeConversationId !== convId) {
            byConversation = writeView(byConversation, s.activeConversationId, {
              selectedId: s.selectedId,
              pinnedIds: s.pinnedIds,
              collapsedIds: s.collapsedIds,
            });
          }
          // 들어오는 대화 뷰 복원(저장본 또는 빈 뷰) → 현재 그래프 노드에 맞게 정리.
          const saved = (convId && byConversation[convId]) || EMPTY_VIEW;
          const view = pruneView(saved, graph);
          byConversation = writeView(byConversation, convId, view);
          return {
            graph: overlayViewState(graph, view.pinnedIds, view.collapsedIds),
            activeConversationId: convId,
            byConversation,
            selectedId: view.selectedId,
            pinnedIds: view.pinnedIds,
            collapsedIds: view.collapsedIds,
          };
        }),
      select: (id) =>
        set((s) => ({
          selectedId: id,
          graph: { ...s.graph, lastActiveId: id },
          byConversation: writeView(s.byConversation, s.activeConversationId, {
            selectedId: id,
            pinnedIds: s.pinnedIds,
            collapsedIds: s.collapsedIds,
          }),
        })),
      togglePin: (id) =>
        set((s) => {
          const pinnedIds = toggleId(s.pinnedIds, id);
          return {
            pinnedIds,
            graph: overlayViewState(s.graph, pinnedIds, s.collapsedIds),
            byConversation: writeView(s.byConversation, s.activeConversationId, {
              selectedId: s.selectedId,
              pinnedIds,
              collapsedIds: s.collapsedIds,
            }),
          };
        }),
      toggleCollapse: (id) =>
        set((s) => {
          const collapsedIds = toggleId(s.collapsedIds, id);
          return {
            collapsedIds,
            graph: overlayViewState(s.graph, s.pinnedIds, collapsedIds),
            byConversation: writeView(s.byConversation, s.activeConversationId, {
              selectedId: s.selectedId,
              pinnedIds: s.pinnedIds,
              collapsedIds,
            }),
          };
        }),
      reset: () =>
        set((s) => ({
          graph: { ...EMPTY_GRAPH, nodes: [] },
          activeConversationId: null,
          selectedId: null,
          pinnedIds: [],
          collapsedIds: [],
          byConversation: s.byConversation,
        })),
      resetForAccountSwitch: () =>
        set(() => ({
          graph: { ...EMPTY_GRAPH, nodes: [] },
          activeConversationId: null,
          selectedId: null,
          pinnedIds: [],
          collapsedIds: [],
          byConversation: {},
        })),
    }),
    {
      name: STORAGE_KEY,
      // 영속 = 대화별 뷰상태만. 활성 flat/graph 는 setGraph 가 매 진입 시 재구성.
      partialize: (state) => ({ byConversation: state.byConversation }),
    }
  )
);
